import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { QRCodeComponent } from 'angularx-qrcode';

import { LanguageService } from '../services/language.service';
import { PublicSettlementService } from '../services/public-settlement.service';
import { Lang } from '../i18n/translations';
import {
  NothingDueReason,
  SettlementPhase,
  SettlementQuote,
  SettlementReceipt,
} from '../models/settlement.models';

/**
 * Settlement page — what the exit-display QR points at.
 *
 * A free-flow site has no barrier to hold anyone: the parker drives out and the stay ends unpaid
 * (KAN-458 D-21). This page is how they settle it from their own phone, with nothing but the link
 * in the QR. It is deliberately **not** station-specific — no terminal id, no space, no login. The
 * parking-process id in the URL is the whole identity of the transaction.
 *
 * Three rules this page is built around:
 *
 *  1. **The server owns the amount.** Every figure comes from `quote/{id}` and the same figure is
 *     handed back on start, where a mismatch is refused. The page cannot invent, round, or discount.
 *  2. **A redirect is a hint, never proof.** Coming back with `?paid=1` only means Stripe sent the
 *     browser here — anyone can type that. Payment is confirmed by re-reading the quote until the
 *     server says nothing is owed.
 *  3. **Nothing owed is a normal answer.** Already paid, covered by a pass, inside the grace window:
 *     each gets its own message, and none of them opens a checkout.
 */
@Component({
  selector: 'app-settlement',
  standalone: true,
  imports: [CommonModule, QRCodeComponent],
  templateUrl: './settlement.component.html',
  styleUrl: './settlement.component.scss',
})
export class SettlementComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(PublicSettlementService);
  readonly i18n = inject(LanguageService);

  readonly phase = signal<SettlementPhase>('loading');
  readonly quote = signal<SettlementQuote | null>(null);
  readonly receipt = signal<SettlementReceipt | null>(null);

  /** Free-text problem shown under the header. Already translated when set. */
  readonly errorMessage = signal<string | null>(null);

  /** Set after a `?cancelled=1` return so the parker sees why they are back on the amount screen. */
  readonly wasCancelled = signal(false);

  /**
   * True once the confirm poll has given up. The parker is told the payment has not landed *yet* —
   * never that it failed, because a slow webhook is far more likely than a lost charge, and telling
   * someone their payment failed is how a second charge gets made.
   */
  readonly confirmTimedOut = signal(false);

  /**
   * Set when this payment has no fiscal receipt to fetch. Distinct from a failed receipt: nothing
   * went wrong — online payments are not fiscalised, that is the decision. Either way the parker
   * stops waiting for one.
   */
  readonly receiptUnavailable = signal(false);

  /**
   * The parker opened the receipt, on this visit (the tap) or an earlier one (the receipt read answers
   * 404). The link opens in a new tab, so this page has to drop the QR and the link itself: left on
   * screen they would offer a receipt the backend no longer hands out.
   */
  readonly receiptOpened = signal(false);

  /** Caller return URL (e.g. Parker App) when invoked with `?returnUrl=...`. */
  readonly returnUrl = signal<string | null>(null);

  /** Countdown seconds for auto-redirect after successful payment. */
  readonly redirectCountdown = signal<number | null>(null);

  private parkingProcessId = '';
  /** The session whose receipt is on screen; marked downloaded when the parker opens it. */
  private receiptSessionId: string | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private receiptTimer: ReturnType<typeof setTimeout> | null = null;
  private redirectInterval: ReturnType<typeof setInterval> | null = null;

  /** How long to wait for the payment webhook after the Stripe return, and how often to re-check. */
  private static readonly ConfirmPollIntervalMs = 2000;
  private static readonly ConfirmPollAttempts = 15;

  /** The fiscal receipt is signed asynchronously by the EFSTA worker; poll a bounded number of times. */
  private static readonly ReceiptPollIntervalMs = 3000;
  private static readonly ReceiptPollAttempts = 10;

  // ------------------------------------------------------------------ derived display values

  readonly amountLabel = computed(() => this.money(this.quote()?.amountDue));
  readonly grossLabel = computed(() => this.money(this.quote()?.grossAmount));
  readonly paidLabel = computed(() => this.money(this.quote()?.netPaidAmount));

  /** Only shown when something was already paid — an unqualified "€0.00 paid" row is just noise. */
  readonly hasPartialPayment = computed(() => (this.quote()?.netPaidAmount ?? 0) > 0);

  readonly startedAtLabel = computed(() => this.instant(this.quote()?.startedAt));
  readonly endedAtLabel = computed(() => this.instant(this.quote()?.endedAt));

  /** Stay length, from the two instants the server priced against. Empty while the stay is open. */
  readonly durationLabel = computed(() => {
    const q = this.quote();
    if (!q?.startedAt || !q?.endedAt) return '';

    const from = new Date(q.startedAt).getTime();
    const to = new Date(q.endedAt).getTime();
    if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return '';

    const minutes = Math.floor((to - from) / 60000);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h} h ${m} min` : `${m} min`;
  });

  /**
   * Which "nothing to pay" message applies. Derived from the quote source so the page states the
   * actual reason rather than a catch-all — see {@link NothingDueReason}.
   */
  readonly nothingDueReason = computed<NothingDueReason>(() => {
    const q = this.quote();
    switch (q?.source) {
      case 'AlreadySettled':
        return 'alreadyPaid';
      case 'CoveredZero':
        return 'covered';
      case 'NotPriceable':
        return 'notPriceable';
      default:
        // Priced, but the total came to nothing — a stay inside the free grace window.
        return (q?.netPaidAmount ?? 0) > 0 ? 'alreadyPaid' : 'freeStay';
    }
  });

  /** What the receipt QR encodes. Null until EFSTA answers; the paid screen shows a spinner meanwhile. */
  readonly receiptQrData = computed(() => this.receipt()?.url ?? null);

  // ------------------------------------------------------------------ lifecycle

  ngOnInit(): void {
    // The kiosk hides the cursor globally (`styles.scss`) because the column's touchscreen also
    // registers as a mouse. This page is a phone/desktop browser, so opt out of that rule.
    document.body.classList.add('pay-page');

    this.parkingProcessId = this.route.snapshot.paramMap.get('parkingProcessId') ?? '';
    this.returnUrl.set(this.route.snapshot.queryParamMap.get('returnUrl') || null);

    // Language, most explicit signal first:
    //   1. ?lang= — carried back on the Stripe return URL, so a parker who switched to English does
    //      not land on a German confirmation screen. Also lets a QR encode a language outright.
    //   2. the browser's own — this page is opened by QR on the parker's phone, where there is no
    //      station config to inherit from. Without it every parker got German whatever they speak.
    //   3. the service default (German), via setFromBrowser's own fallback.
    // A manual DE/EN tap always overrides whatever was chosen here.
    const langParam = this.route.snapshot.queryParamMap.get('lang');
    if (langParam === 'de' || langParam === 'en') {
      this.i18n.set(langParam);
    } else {
      this.i18n.setFromBrowser();
    }

    if (!this.parkingProcessId) {
      this.fail(this.i18n.t('pay.error.badLink'));
      return;
    }

    if (this.route.snapshot.queryParamMap.get('cancelled') === '1') {
      this.wasCancelled.set(true);
    }

    // A `?paid=1` return means Stripe sent us here — not that the money arrived. Wait for the
    // server to agree before showing a success screen.
    if (this.route.snapshot.queryParamMap.get('paid') === '1') {
      this.phase.set('confirming');
      // Nudge the server to read the provider now rather than waiting on the webhook or the
      // five-minute sweep; the poll below still decides, this only stops it waiting minutes.
      void this.service
        .reconcile(this.parkingProcessId)
        .finally(() => void this.pollUntilSettled(SettlementComponent.ConfirmPollAttempts));
      return;
    }

    void this.loadQuote();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('pay-page');
    this.clearTimers();
  }

  // ------------------------------------------------------------------ actions

  /** Fetch (or re-fetch) the quote and route to the screen it implies. */
  async loadQuote(): Promise<void> {
    this.phase.set('loading');
    this.errorMessage.set(null);

    try {
      const q = await this.service.quote(this.parkingProcessId);
      this.quote.set(q);
      this.phase.set(q.amountDue > 0 ? 'quote' : 'nothingDue');
      if (q.amountDue <= 0 && this.returnUrl()) {
        this.startAutoRedirect();
      }
    } catch (err) {
      this.fail(this.messageFor(err, this.i18n.t('pay.error.load')));
    }
  }

  /**
   * Hand the browser to Stripe for the amount currently on screen.
   *
   * If the server has since repriced the stay it refuses the start ("Amount Changed"); the page
   * re-quotes and shows the new figure rather than charging silently.
   */
  async pay(): Promise<void> {
    const q = this.quote();
    if (!q || q.amountDue <= 0) return;

    this.phase.set('redirecting');
    this.errorMessage.set(null);
    this.wasCancelled.set(false);

    // Built from where this page is ACTUALLY served, not from config: Stripe must return the parker
    // to the origin they are browsing, or they come back to a page with none of their session state.
    //
    // That couples it to the backend's `ParkingProcess:PublicEndpoint:PaymentPageBaseUrl`, which is
    // the allow-list the redirect guard checks. The two must name the same origin, and the failure is
    // a flat "Redirect Not Allowed" with no hint as to why — serving this page on `localhost` while
    // the backend is configured for a LAN host is the usual way in. Fix the deployment, not this line.
    const base = `${window.location.origin}/pay/${this.parkingProcessId}`;
    const lang = `lang=${this.i18n.lang()}`;
    const retParam = this.returnUrl() ? `&returnUrl=${encodeURIComponent(this.returnUrl()!)}` : '';

    try {
      const start = await this.service.startCheckout(
        q,
        `${base}?paid=1&${lang}${retParam}`,
        `${base}?cancelled=1&${lang}${retParam}`
      );

      if (!start.checkoutUrl) {
        // A session with no hosted URL is not something the parker can act on. Fail visibly rather
        // than leaving them on a dead "redirecting" screen.
        this.phase.set('quote');
        this.errorMessage.set(this.i18n.t('pay.error.noCheckoutUrl'));
        return;
      }

      window.location.href = start.checkoutUrl;
    } catch (err) {
      const body = this.errorBody(err);

      if (body.includes('Amount Changed') || body.includes('Currency Changed')) {
        this.errorMessage.set(this.i18n.t('pay.error.amountChanged'));
        await this.loadQuote();
        return;
      }

      if (body.includes('Nothing To Pay')) {
        // The stay was settled on another rail (the column, or a second phone) between the quote
        // and the tap. That is the double-payment guard working, so show it as good news.
        await this.loadQuote();
        return;
      }

      if (body.includes('Redirect Not Allowed')) {
        // The backend refused our success/cancel URL because this page is not being served from the
        // configured payment-page origin (ParkingProcess:PublicEndpoint:PaymentPageBaseUrl). It is an
        // anti-open-redirect guard, so it is doing its job — but the parker sees only "could not
        // start" and has no way to know the page itself is the problem.
        //
        // Two ways to get here. A parker who followed a hand-copied or rewritten link rather than the
        // QR — hence the "scan the QR code again" wording. Or a misconfigured environment: serving the
        // page on `localhost` while the backend is configured for the LAN host is the one that costs
        // developer time, so it is named explicitly in the console rather than left to be rediscovered.
        console.error(
          '[settlement] Redirect refused: this page origin (%s) is not the configured ' +
            'PaymentPageBaseUrl. Serve it on the configured host — "localhost" never matches a LAN-configured backend.',
          window.location.origin,
        );
        this.phase.set('quote');
        this.errorMessage.set(this.i18n.t('pay.error.redirectBlocked'));
        return;
      }

      this.phase.set('quote');
      this.errorMessage.set(this.messageFor(err, this.i18n.t('pay.error.start')));
    }
  }

  /** Manual re-check from the "not confirmed yet" screen. Never re-opens a checkout. */
  async recheck(): Promise<void> {
    this.confirmTimedOut.set(false);
    this.phase.set('confirming');
    await this.service.reconcile(this.parkingProcessId);
    await this.pollUntilSettled(SettlementComponent.ConfirmPollAttempts);
  }

  setLang(lang: Lang): void {
    this.i18n.set(lang);
  }

  returnToCaller(): void {
    this.clearTimers();
    const url = this.returnUrl();
    if (url) {
      window.location.href = url;
    } else {
      window.history.back();
    }
  }

  cancelAutoRedirect(): void {
    if (this.redirectInterval) {
      clearInterval(this.redirectInterval);
      this.redirectInterval = null;
    }
    this.redirectCountdown.set(null);
  }

  private startAutoRedirect(): void {
    this.cancelAutoRedirect();
    let secondsLeft = 5;
    this.redirectCountdown.set(secondsLeft);
    this.redirectInterval = setInterval(() => {
      secondsLeft--;
      if (secondsLeft <= 0) {
        this.cancelAutoRedirect();
        this.returnToCaller();
      } else {
        this.redirectCountdown.set(secondsLeft);
      }
    }, 1000);
  }

  // ------------------------------------------------------------------ internals

  /**
   * Re-read the quote until the server reports nothing owed, or the attempts run out.
   *
   * This is the only thing that turns the page green. A Stripe redirect races the webhook that
   * records the payment, so an immediate read can legitimately still show the amount as due.
   */
  private async pollUntilSettled(attemptsLeft: number): Promise<void> {
    this.clearTimers();

    try {
      const q = await this.service.quote(this.parkingProcessId);
      this.quote.set(q);

      if (q.amountDue <= 0) {
        this.phase.set('paid');
        void this.loadReceipt(SettlementComponent.ReceiptPollAttempts);
        if (this.returnUrl()) {
          this.startAutoRedirect();
        }
        return;
      }
    } catch {
      // Swallow and retry: a transient read failure during confirmation must not be reported as a
      // failed payment. If the attempts run out the parker gets the neutral "not yet" screen.
    }

    if (attemptsLeft <= 1) {
      this.confirmTimedOut.set(true);
      return;
    }

    this.pollTimer = setTimeout(
      () => void this.pollUntilSettled(attemptsLeft - 1),
      SettlementComponent.ConfirmPollIntervalMs,
    );
  }

  /**
   * Poll the fiscal receipt while the EFSTA worker is still signing it. Best-effort — never blocks.
   *
   * An online payment gets Stripe's own receipt, not an EFSTA one — fiscalisation is a terminal-rail
   * feature by decision (D-44). The endpoint answers with whichever exists and flags which it is, so
   * the page can call a tax document and a proof of payment by their right names.
   */
  private async loadReceipt(attemptsLeft: number): Promise<void> {
    try {
      const session = await this.service.latestSession(this.parkingProcessId);
      if (!session) {
        this.receiptUnavailable.set(true);
        return;
      }

      const r = await this.service.receipt(session.id);
      this.receiptSessionId = session.id;
      this.receipt.set(r);

      if (r.status === 'Ready' || r.status === 'Failed') return;

      if (attemptsLeft <= 1) {
        this.receiptUnavailable.set(true);
        return;
      }
    } catch (err) {
      // 404 for a session we just looked up means the parker already opened this receipt: retrying
      // cannot change that, so say so at once.
      if (err instanceof HttpErrorResponse && err.status === 404) {
        this.receiptOpened.set(true);
        return;
      }
      if (attemptsLeft <= 1) {
        this.receiptUnavailable.set(true);
        return;
      }
    }

    this.receiptTimer = setTimeout(
      () => void this.loadReceipt(attemptsLeft - 1),
      SettlementComponent.ReceiptPollIntervalMs,
    );
  }

  /**
   * The parker tapped "open receipt". From now on the receipt is no longer handed out anonymously, so
   * whoever learns this session id later cannot fetch it. Marked on the tap rather than on the read, so
   * a reload before the parker opened it loses nothing. Fire-and-forget: the link opens either way.
   *
   * The page updates itself rather than reloading: a reload would cancel the mark while it is in flight.
   */
  onReceiptOpened(): void {
    this.receiptOpened.set(true);
    if (!this.receiptSessionId) return;
    this.service
      .markReceiptDownloaded(this.receiptSessionId, this.parkingProcessId)
      .catch(() => { /* the parker already has the link; a lost mark only leaves it reachable */ });
  }

  private clearTimers(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    if (this.receiptTimer) clearTimeout(this.receiptTimer);
    if (this.redirectInterval) clearInterval(this.redirectInterval);
    this.pollTimer = null;
    this.receiptTimer = null;
    this.redirectInterval = null;
  }

  private fail(message: string): void {
    this.errorMessage.set(message);
    this.phase.set('error');
  }

  /** Raw error body as a string. The API returns `"Title.Detail"` text for 400/404. */
  private errorBody(err: unknown): string {
    const body = (err as { error?: unknown })?.error;
    return typeof body === 'string' ? body : '';
  }

  private messageFor(err: unknown, fallback: string): string {
    const status = (err as { status?: number })?.status;
    if (status === 404) return this.i18n.t('pay.error.notFound');
    if (status === 429) return this.i18n.t('pay.error.tooMany');
    return fallback;
  }

  /** Amounts are formatted in the parker's language with the currency the server priced in. */
  private money(amount?: number | null): string {
    const currency = this.quote()?.currency || 'EUR';
    const locale = this.i18n.lang() === 'de' ? 'de-AT' : 'en-GB';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount ?? 0);
  }

  private instant(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const locale = this.i18n.lang() === 'de' ? 'de-AT' : 'en-GB';
    return d.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
